import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/design-system";
import { DocumentViewer } from "@/components/ui/document-workspace";
import { errorMessage } from "@/lib/api";
import { siteEquipmentApi, type EquipmentDocument } from "@/lib/api/oprema";
import { equipmentE2Api } from "@/lib/api/oprema-e2";
import { useEquipmentAccess } from "./shared";

export function EquipmentDocumentActions({
  document,
  compact = false,
}: {
  document: EquipmentDocument;
  compact?: boolean;
}) {
  const access = useEquipmentAccess();
  const isE2 = document.content_url?.startsWith("/api/oprema/e2/dokumenti/") ?? false;
  const canAttach = isE2 ? access.maintain : access.maintain || access.damage || access.receive;
  const [url, setUrl] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const resource = useRef<string | null>(null);
  const controller = useRef<AbortController | null>(null);
  const signedInput = useRef<HTMLInputElement>(null);
  useEffect(
    () => () => {
      controller.current?.abort();
      if (resource.current) URL.revokeObjectURL(resource.current);
    },
    [document.id],
  );
  const close = () => {
    if (resource.current) URL.revokeObjectURL(resource.current);
    resource.current = null;
    setUrl("");
  };
  async function open() {
    if (busy) return;
    setBusy(true);
    setError("");
    controller.current?.abort();
    const request = new AbortController();
    controller.current = request;
    try {
      const blob = await siteEquipmentApi.document(document.id, request.signal, document.content_url);
      if (!request.signal.aborted) {
        if (resource.current) URL.revokeObjectURL(resource.current);
        resource.current = URL.createObjectURL(blob);
        setUrl(resource.current);
      }
    } catch (cause) {
      if (!request.signal.aborted)
        setError(errorMessage(cause, "Dokument nije dostupan."));
    } finally {
      if (!request.signal.aborted) setBusy(false);
    }
  }
  return (
    <div className={compact ? "equipment-document-inline" : "equipment-card"}>
      {!compact && <b>{document.broj}</b>}
      <Button variant="outline" disabled={busy} onClick={() => void open()}>
        {compact ? "PDF" : "Otvori / Ispiši"}
      </Button>
      {canAttach && (
        <>
          <Button
            type="button"
            variant="outline"
            disabled={busy}
            onClick={() => signedInput.current?.click()}
          >
            Priloži potpisani
          </Button>
          <input
            ref={signedInput}
            hidden
            aria-label={`Priloži potpisani: ${document.broj}`}
            type="file"
            accept="image/jpeg,image/png"
            disabled={busy}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              setBusy(true);
              setError("");
              setMessage("");
              void (isE2
                ? equipmentE2Api.signed(document.id, file)
                : siteEquipmentApi.attachSigned(document.kretanje_id, file))
                .then(() => setMessage("Potpisani dokument priložen."))
                .catch((cause) =>
                  setError(errorMessage(cause, "Prilog nije spremljen.")),
                )
                .finally(() => setBusy(false));
              e.target.value = "";
            }}
          />
        </>
      )}
      {error && <p role="alert">{error}</p>}
      {message && <p role="status">{message}</p>}
      {url && (
        <Dialog
          fullscreen
          className="equipment-document-dialog"
          title={document.broj}
          onClose={close}
        >
          <div className="equipment-actions">
            <Button
              variant="outline"
              onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
            >
              Otvori za ispis
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                const download = window.document.createElement("a");
                download.href = url;
                download.download = `${document.broj}.pdf`;
                download.click();
              }}
            >
              Preuzmi PDF
            </Button>
          </div>
          <DocumentViewer
            url={url}
            mime="application/pdf"
            title={document.broj}
          />
        </Dialog>
      )}
    </div>
  );
}
